import classnames from 'classnames';
import {filter, get, isEmpty, isEqual, map, mapValues, negate, sortBy} from 'lodash';
import React, {useState} from 'react';
import {DragDropContext, Droppable} from '@hello-pangea/dnd';
import CollegeRow from 'shared/application-management/components/college-row';
import Card from 'src/components/Cards/Card';
import CollapsibleSection from 'src/components/collapsible-section';
import DiscoverCollegesButton from 'shared/components/discover-colleges-button';
import Dropdown from 'src/components/dropdown';
import {convertMetersToMiles, HS_SORT_OPTIONS, SORT_TO_FIELD} from 'shared/utils';
import {track} from 'src/utils/analytics';
import {ALL, EARNING_FILTERS, earningFilterOptions, orderedFilters} from '../filters';
import styles from './my-list-section.module.scss';

export interface EarningsRelationship {
  amount: number | null;
  churnedAt: string | null;
  deadlineDay: number | null;
  deadlineMonth: number | null;
  deadlineTerm: string | null;
  deadlineTermYear: number | null;
  deadlineYear: number | null;
  hasEarnings: boolean;
  minGPA: number | null;
  replacementAward: $TSFixMe | null;
  showSubmissionTerm: boolean;
  spotAward: $TSFixMe | null;
  statusType: string;
  termDeferred: boolean | null;
  showTooltipFlag: boolean | null;
  useApplyByTerminology: boolean | null;
}

interface CollegeEarning {
  _id: string;
  acceptanceRate: number;
  amount: number;
  applicationWebsite: string;
  averageNetPrice: number;
  banner: string;
  city: string;
  control: string;
  distance: number;
  divisor: number;
  earningRelationship: EarningsRelationship;
  forbesRank: number;
  logo: string;
  maxEarnings: number;
  name: string;
  nickname: string;
  sixYearGraduationRate: number;
  slug: string;
  state: string;
  tuition: number;
  usNewsRank: number;
  useApplyByTerminology: boolean;
  admissionLikelihoodScoreCategory: string | null;
  applicationStatus?: $TSFixMe;
}

const isPresent = negate(isEmpty);

const SORT_OPTIONS = [{value: 'Sort: My order', type: 'user', order: 'asc'}, ...HS_SORT_OPTIONS];

const SORT_FIELD_MAPPING = {
  ...SORT_TO_FIELD,
  earnings: 'amount',
};

const orderingDirection = (order: string) => (order === 'asc' ? 1 : -1);

// Sort earnings with order, but null/undefined goes last
const sortFunctionFor =
  ({type, order}: {value: string; type: string; order: string}) =>
  (earning: CollegeEarning) =>
    orderingDirection(order) * get(earning, SORT_FIELD_MAPPING[type] || type) || Infinity;

const onDragEnd =
  ({collegeEarnings, receiveEarningsOrder, updateEarningsOrder, studentId}) =>
  (result) => {
    const {destination, source} = result;

    // Don't save if the user drags the element to an invalid location.
    if (!destination) return;

    // Don't save if the user drags the element to its current location.
    if (destination.droppableId === source.droppableId && destination.index === source.index)
      return;

    const earningsOrder = collegeEarnings.slice();
    earningsOrder.splice(source.index, 1);
    earningsOrder.splice(destination.index, 0, collegeEarnings[source.index]);

    track('my-list-order-event', {
      order: JSON.stringify(map(earningsOrder, '_id')),
      studentId,
    });

    receiveEarningsOrder(earningsOrder);
    updateEarningsOrder(earningsOrder);
  };

interface Props {
  name: string;
  collegeEarnings: CollegeEarning[];
  emptyContent: React.ReactNode;
  setCollegeAppStatus: () => void;
  receiveEarningsOrder: () => void;
  updateEarningsOrder: () => void;
  studentId?: string;
  reorderingEnabled: boolean;
  isApplyButtonShown: boolean;
  isApplicationStatusMenuShown: boolean;
  isDiscoverCollegesButtonShown: boolean;
  isBalancedListInfoAvailable: boolean;
}

const MyListSection = ({
  collegeEarnings = [],
  emptyContent = null,
  isApplicationStatusMenuShown,
  isApplyButtonShown,
  isDiscoverCollegesButtonShown,
  name,
  reorderingEnabled,
  setCollegeAppStatus,
  studentId,
  receiveEarningsOrder,
  updateEarningsOrder,
  isBalancedListInfoAvailable,
}: Props) => {
  const [sort, setSort] = useState(SORT_OPTIONS[0]);

  const sortingDropdown = (
    <Dropdown
      className={classnames(styles.dropdown, styles.sortingDropdown)}
      onChange={(e) => {
        const option = SORT_OPTIONS.find((o) => `${o.type}|||${o.order}` === e.target.value);

        if (option) {
          setSort(option);
        }
      }}
      options={SORT_OPTIONS.map((option) => ({
        value: `${option.type}|||${option.order}`,
        text: option.value,
        _id: `${option.type}|||${option.order}`,
      }))}
      value={`${sort.type}|||${sort.order}`}
      testid="sort-dropdown"
    />
  );

  const [filterBy, setFilterBy] = useState(ALL);
  const filterFn = EARNING_FILTERS[filterBy].filter;
  const itemCounts = mapValues(
    EARNING_FILTERS,
    (earningFilter) => filter(collegeEarnings, earningFilter.filter).length
  );
  const filters = orderedFilters(isBalancedListInfoAvailable);
  const filterOptions = earningFilterOptions({filters, itemCounts});

  const filteringDropdown = (
    <Dropdown
      className={styles.dropdown}
      options={filterOptions}
      required
      onChange={(e) => setFilterBy(e.target.value)}
      value={filterBy}
      testid="filter-dropdown"
    />
  );

  const filteredEarnings = filter(collegeEarnings, filterFn);

  const unfilteredAndUnsorted = isEqual(sort, SORT_OPTIONS[0]) && filterBy === ALL;

  const collegeRows = map(sortBy(filteredEarnings, sortFunctionFor(sort)), (earning, index) => {
    const {
      banner,
      logo,
      city,
      state,
      slug,
      applicationStatus,
      averageNetPrice,
      control,
      distance,
      applicationWebsite,
      admissionLikelihoodScoreCategory,
    } = earning;

    const milesAway = convertMetersToMiles(distance);
    const collegeFacts = {
      collegeCategory: control,
      distanceMiles: milesAway,
      yearlyNetPriceDollars: averageNetPrice,
    };
    const location = `${city}, ${state}`;

    return (
      <CollegeRow
        key={slug}
        index={index}
        name={earning.name}
        slug={slug}
        location={location}
        bannerImage={banner}
        logoImage={logo}
        earnings={earning}
        draggable={reorderingEnabled && unfilteredAndUnsorted}
        collegeFacts={collegeFacts}
        applicationWebsite={applicationWebsite}
        applicationStatus={applicationStatus}
        setCollegeAppStatus={setCollegeAppStatus}
        isApplyButtonShown={isApplyButtonShown}
        isApplicationStatusMenuShown={isApplicationStatusMenuShown}
        admissionLikelihoodCategory={admissionLikelihoodScoreCategory}
        isBalancedListInfoAvailable={isBalancedListInfoAvailable}
        isLast={index === collegeEarnings.length - 1}
      />
    );
  });

  const subtitle = `${collegeRows.length} colleges`;
  let content: React.ReactNode;

  if (isPresent(collegeRows)) {
    content = collegeRows;
  } else if (isPresent(emptyContent)) {
    content = <div className={styles.emptyContent}>{emptyContent}</div>;
  } else {
    throw 'Please pass either `collegeEarnings` or `emptyContent` to MyListSection';
  }

  const totalColleges = collegeEarnings.length;
  const showFilteringAndSorting = totalColleges > 6;

  return (
    <Card className={styles.myListSection}>
      <CollapsibleSection
        title={name}
        titleTestid="my-list-colleges-title"
        subtitle={subtitle}
        subtitleTestid="my-list-colleges-subtitle"
        isOpen
      >
        {showFilteringAndSorting && (
          <div className={styles.filterAndSort}>
            {filteringDropdown}
            {sortingDropdown}
          </div>
        )}
        <DragDropContext
          onDragEnd={onDragEnd({
            collegeEarnings,
            receiveEarningsOrder,
            updateEarningsOrder,
            studentId,
          })}
        >
          <Droppable droppableId="my-list-droppable">
            {(provided) => (
              <div
                {...provided.droppableProps}
                className={styles.droppableContainer}
                ref={provided.innerRef}
              >
                {content}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {isDiscoverCollegesButtonShown && <DiscoverCollegesButton />}
      </CollapsibleSection>
    </Card>
  );
};

const Loading = () => (
  <Card>
    <div className={classnames('title', 'shared-shimmer-text', 'medium')} />
    <div className={classnames('subtitle', 'shared-shimmer-text', 'x-small')} />
    <CollegeRow.Loading />
    <CollegeRow.Loading />
    <CollegeRow.Loading />
  </Card>
);

MyListSection.Loading = Loading;

export default MyListSection;
